import { useId } from 'react';
import { HelpTip } from '../HelpTip';
import { Select } from './Select';
import type { SelectOption } from './selectShared';
import { useDeferredError } from './useDeferredError';

export interface FieldGroupSegment {
  /**
   * The input's own DOM id, and it must match the server's field name through the
   * `${idPrefix}-${kebab(field)}` convention.
   *
   * **A group never collapses two fields into one id.** `focusValidationError` lands on the
   * control the server named, so a rejected `suffix` has to have a `#…-suffix` to land on —
   * joining the two visually must not take that away.
   */
  id: string;
  /**
   * What this segment holds.
   *
   * The **first** segment is named by the group's own visible label and takes no placeholder,
   * because it is what the group is about. Every **later** segment names itself — this string
   * becomes its `aria-label` and its placeholder — since the group has exactly one label line
   * and a second one would break the alignment that rule exists for.
   */
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Overrides the placeholder a later segment takes from its label. */
  placeholder?: string;
  /** Field-level message from the server or client validation. */
  error?: string;
  required?: boolean;
  disabled?: boolean;
  autoComplete?: string;
  maxLength?: number;
  /**
   * Turns this segment into a dropdown — a `<Select seamless>`, not a second kind of control.
   *
   * `seamless` exists for exactly this: it drops the border, the ground and the radius so the
   * GROUP owns them, which is the same contract the text segments keep. Hand-rolling a
   * `<select>` here would put a third picker treatment in the product.
   *
   * A segment with options ignores `maxLength` and `autoComplete`; it cannot hold a value that
   * is not on the list.
   */
  options?: SelectOption[];
  /** For an options segment: the placeholder shown while nothing is chosen. */
  emptyLabel?: string;
  /**
   * Flex sizing. The principal segment grows; an addon stays narrow and does not shrink.
   * Defaults to `min-w-0 flex-1`.
   */
  width?: string;
}

/**
 * Two or more inputs that are **one value**, joined into one control.
 *
 * A surname and its suffix are one name — "Dela Cruz Jr." — so they read as one field with
 * two boxes rather than as two fields that happen to sit near each other. Two independent
 * facts are two fields; this is not a layout device for saving a grid slot.
 *
 * The wrapper owns the border and the focus ring and the segments are borderless with a
 * `border-l` divider, which is the composition `<SearchGroup>` already uses — so a joined
 * form field and a joined toolbar strip read as the same kind of control.
 *
 * Every promise the other shared inputs make still holds: one label line plus the control, so
 * a row of fields aligns; the description in a `<HelpTip>` with an `sr-only` twin wired to
 * `aria-describedby`; and errors below the control, in the flow, in red.
 */
export function FieldGroup({
  label,
  description,
  segments,
  labelFor,
  className = '',
}: {
  label: string;
  /**
   * One description for the whole group, in the group's help icon.
   *
   * Never one per segment: two help icons in a label row leave the reader working out which
   * belongs to which box, and the note that matters is usually about the addon anyway.
   */
  description?: string;
  segments: FieldGroupSegment[];
  /**
   * Which segment the visible label names. Defaults to the FIRST, which is right whenever the
   * group leads with its principal — a surname followed by its suffix.
   *
   * It exists for the group that does NOT: a salutation sits to the LEFT of the first name,
   * so without this the label "First name" would be wired to the title box, and a screen
   * reader would announce the dropdown as the first-name field. Pass the principal's id.
   */
  labelFor?: string;
  className?: string;
}) {
  const generatedId = useId();
  const descriptionId = `${generatedId}-desc`;

  // The group is required when any part of it is — one fact, so one asterisk.
  const required = segments.some((segment) => segment.required);

  // The segment the visible label names — the principal, not necessarily the leftmost.
  const labelled = labelFor ?? segments[0]?.id;

  /*
    A required-but-empty field must not open red, and a group is ONE control — so the
    deferral is the group's, not each segment's. The segments' messages are joined into one
    signature and deferred together: revealing Last name while Suffix stayed silent would
    point at one box and leave the other's rejection unsaid, which is the thing this
    component's error rule exists to prevent.

    Undefined when nothing is wrong, so an error that appears later differs from the mount
    value and is shown at once — a server rejection included. See `useDeferredError`.
  */
  const signature = segments.some((segment) => segment.error)
    ? segments.map((segment) => segment.error ?? '').join('\u001f')
    : undefined;

  const { error: shownSignature, markTouched } = useDeferredError(signature);
  const showErrors = shownSignature !== undefined;

  const errorOf = (segment: FieldGroupSegment) => (showErrors ? segment.error : undefined);
  const invalid = showErrors;

  return (
    <div className="flex flex-col gap-1.5">
      {/* Labels are regular weight and exactly one line tall, so a row of fields aligns. */}
      <div className="flex min-h-5 items-center gap-1.5">
        <label htmlFor={labelled} className="text-sm text-slate-700">
          {label}
          {required ? (
            <span className="ml-0.5 text-red-600" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>

        {description ? <HelpTip label={description} /> : null}
      </div>

      {description ? (
        <span id={descriptionId} className="sr-only">
          {description}
        </span>
      ) : null}

      <div
        className={[
          'flex min-h-10 items-stretch rounded-lg border bg-white transition',
          invalid
            ? 'border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-100'
            : 'border-slate-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {segments.map((segment, index) => {
          const first = index === 0;
          const isLabelled = segment.id === labelled;
          const last = index === segments.length - 1;
          const describedBy = [
            description ? descriptionId : null,
            errorOf(segment) ? `${segment.id}-err` : null,
          ]
            .filter(Boolean)
            .join(' ');

          const edge = [
            // The radius belongs to whichever segment is on the edge, or a corner curve cuts
            // across the box beside it.
            first ? 'rounded-l-lg' : `border-l ${invalid ? 'border-red-400' : 'border-slate-300'}`,
            last ? 'rounded-r-lg' : '',
          ]
            .filter(Boolean)
            .join(' ');

          if (segment.options) {
            return (
              <div key={segment.id} className={[segment.width ?? 'min-w-0 flex-1', edge].join(' ')}>
                <Select
                  id={segment.id}
                  seamless
                  grouped
                  value={segment.value ? segment.value : null}
                  onChange={segment.onChange}
                  options={segment.options}
                  placeholder={segment.emptyLabel ?? segment.label}
                  disabled={segment.disabled}
                  required={segment.required}
                  /*
                    `ariaLabel`, NOT `label`. `label` renders a VISIBLE label above the control,
                    which inside a group stacks a second line into a row the whole form aligns
                    on — it made the name row 67px against its neighbours' 40px. A group has one
                    label line; a segment that is not the labelled one carries its name
                    invisibly, exactly as the text segments do.
                  */
                  ariaLabel={isLabelled ? undefined : segment.label}
                  sort="none"
                />
              </div>
            );
          }

          return (
            <input
              key={segment.id}
              id={segment.id}
              name={segment.id}
              type="text"
              value={segment.value}
              onChange={(e) => segment.onChange(e.target.value)}
              // Leaving any segment reveals the group's messages — it is one control.
              onBlur={markTouched}
              // The first segment is named by the visible label above it; a later one has to
              // carry its own name, or it is announced as part of the field it sits beside.
              aria-label={isLabelled ? undefined : segment.label}
              placeholder={isLabelled ? segment.placeholder : segment.placeholder ?? segment.label}
              required={segment.required}
              disabled={segment.disabled}
              autoComplete={segment.autoComplete}
              maxLength={segment.maxLength}
              aria-invalid={errorOf(segment) ? true : undefined}
              aria-describedby={describedBy || undefined}
              className={[
                'border-0 bg-transparent px-3 py-2 text-sm text-slate-600',
                'placeholder:text-slate-400 focus:outline-none focus:ring-0',
                'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
                segment.width ?? 'min-w-0 flex-1',
                // One derivation for both branches — a dropdown segment and a text segment
                // that disagreed about which of them owns the corner would show it.
                edge,
              ]
                .filter(Boolean)
                .join(' ')}
            />
          );
        })}
      </div>

      {/*
        One message per wrong segment, in segment order, below the control — an error is not an
        aside, and a group cannot point at one box without leaving the other's rejection unsaid.
      */}
      {segments
        .filter((segment) => errorOf(segment))
        .map((segment) => (
          <p key={segment.id} id={`${segment.id}-err`} className="text-xs text-red-600" role="alert">
            {segment.error}
          </p>
        ))}
    </div>
  );
}
